import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { ENDPOINT, DEACTIVATED_MESSAGE } from 'constants';
import { MemoryClient } from 'utils'; // already used in AxiosInstance
import { triggerAccountDeactivated } from 'utils/deactivation';

// These thunks call the backend with plain axios (not the shared Axios
// instance in services/axios.js), so they bypass that instance's response
// interceptor and need their own deactivation check.
function isDeactivatedError(error) {
  return (
    error.response?.status === 403 &&
    error.response?.data?.message === DEACTIVATED_MESSAGE
  );
}

const baseURL = `${ENDPOINT}api/v1`;

// Login — plain axios, no token yet
//
// Roles are now dynamic DB rows (see Role model), and POST /admin/login
// doesn't include roleDetails on its response (it queries Admin without that
// include) — so there's no role name/list to gate on here any more. Any
// account that authenticates successfully is allowed in; module-level access
// is decided afterwards by GET /admin/my-permissions (see fetchMyPermissions
// below), which is the single source of truth for role/permissions.
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseURL}/admin/login`,
        { email, password },
        { withCredentials: true },
      );

      const data = response.data.data;
      MemoryClient.set('accessToken', data.accessToken);
      MemoryClient.set('refreshToken', data.refreshToken);
      return response.data;
    } catch (error) {
      if (isDeactivatedError(error)) triggerAccountDeactivated();
      return rejectWithValue(error.response?.data || 'Login failed');
    }
  },
);

// my-permissions — fetched right after login / on app load so the sidebar
// and route guards know which modules this admin's role can see.
export const fetchMyPermissions = createAsyncThunk(
  'auth/fetchMyPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseURL}/admin/my-permissions`, {
        withCredentials: true,
        headers: {
          Authorization: MemoryClient.get('accessToken') || '',
        },
      });
      return response.data;
    } catch (error) {
      if (isDeactivatedError(error)) triggerAccountDeactivated();
      return rejectWithValue(error.response?.data || 'Failed to fetch permissions');
    }
  },
);

// fetchUser — Axios instance already sends Authorization header via MemoryClient
export const fetchUser = createAsyncThunk(
  'auth/fetchUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseURL}/admin/me`, {
        withCredentials: true,
        headers: {
          Authorization: MemoryClient.get('accessToken') || '',
        },
      });
      return response.data;
    } catch (error) {
      if (isDeactivatedError(error)) triggerAccountDeactivated();
      return rejectWithValue(error.response?.data || 'Unknown error');
    }
  },
);

// logoutUser — clear MemoryClient on logout
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${baseURL}/admin/logout`, null, {
        withCredentials: true,
        headers: {
          Authorization: MemoryClient.get('accessToken') || '',
        },
      });
      MemoryClient.set('accessToken',  '');
      MemoryClient.set('refreshToken', '');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Logout failed');
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    // Module-level RBAC — populated by fetchMyPermissions. hasFullAccess is
    // true for superAdmin/admin and means "render everything, skip filtering".
    permissions: [],
    hasFullAccess: true,
    // admin/superAdmin accounts only — gates env-variables & account-management.
    isSystemAdmin: false,
    permissionsLoaded: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.permissions = [];
      state.hasFullAccess = true;
      state.isSystemAdmin = false;
      state.permissionsLoaded = false;
      MemoryClient.set('accessToken',  '');
      MemoryClient.set('refreshToken', '');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading        = false;
        state.user           = action.payload.data;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected,  (state, action) => {
        state.loading        = false;
        state.error          = action.payload;
        state.isAuthenticated = false;
      })

      .addCase(fetchUser.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading        = false;
        state.user           = action.payload.data;
        state.isAuthenticated = true;
        // Re-sync MemoryClient in case of page refresh (token came from cookie via /admin/me)
        if (action.payload.data?.accessToken) {
          MemoryClient.set('accessToken', action.payload.data.accessToken);
        }
      })
      .addCase(fetchUser.rejected,  (state, action) => {
        state.loading        = false;
        state.error          = action.payload;
        state.isAuthenticated = false;
      })

      .addCase(logoutUser.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading        = false;
        state.user           = null;
        state.isAuthenticated = false;
        state.permissions     = [];
        state.hasFullAccess   = true;
        state.isSystemAdmin   = false;
        state.permissionsLoaded = false;
      })
      .addCase(logoutUser.rejected,  (state, action) => {
        state.loading        = false;
        state.error          = action.payload;
        state.isAuthenticated = false;
      })

      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        const data = action.payload.data;
        state.permissions       = data?.permissions || [];
        state.hasFullAccess     = Boolean(data?.hasFullAccess);
        state.isSystemAdmin     = Boolean(data?.isSystemAdmin);
        state.permissionsLoaded = true;
      })
      .addCase(fetchMyPermissions.rejected, (state) => {
        // Fail closed: if permissions can't be fetched, don't assume full access.
        state.permissions       = [];
        state.hasFullAccess     = false;
        state.isSystemAdmin     = false;
        state.permissionsLoaded = true;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;